/**
 * Logo Garden module
 *
 * Themed module with optional copy and a grid of logos, 4 up
 */

// Deps
import { createComponent, FelaComponent } from "react-fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { themeText, textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";

// Context
import { getThemeForModule } from "~/context/theme";

// Components
import Visual from "~/components/shared/visual";

export default function LogoGarden({ block }) {
    const {
        heading,
        subheading,
        theme_color,
        logos,
        use_graphic_background,
        background_image,
        anchor_id,
        preload
    } = block;

    let { layout } = block;

    // Default to 5 column layout
    if (!layout.length) layout = "five_col";

    // Determine color theme
    const theme = getThemeForModule(theme_color);
    const textTheme = themeText(theme);
    let backgroundStyles = {
        background: (theme.gradient ? theme.gradient : theme.primary),
        backgroundSize: "auto 100%",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat"
    };

    if (use_graphic_background && !background_image) {
        backgroundStyles.filigree = (theme.filigreeEscaped ? theme.filigreeEscaped : theme.secondaryEscaped);
    }

    if (background_image) {
        backgroundStyles = {
          backgroundImage: "url(" + background_image + ")",
          backgroundSize: "cover",
          backgroundColor: theme.primary
        };
    }

    return (
        <Container id={anchor_id} extend={backgroundStyles} className="module-logo-garden">
            {/* Subheading */}
            {heading && (
                <FelaComponent
                    as="h3"
                    style={combineRules(
                        textStyles.heading3,
                        textTheme,
                        gardenHeadingStyle
                    )}
                    dangerouslySetInnerHTML={{
                        __html: heading
                    }}
                >
                    <span
                    dangerouslySetInnerHTML={{
                        __html: heading
                    }} />
                </FelaComponent>
            )}

            {/* Subheading */}
            {subheading && (
                <FelaComponent
                    as="p"
                    style={combineRules(
                        textStyles.paragraphLarge,
                        textTheme,
                        gardenSubheadingStyle
                    )}
                >
                    <span
                    dangerouslySetInnerHTML={{
                        __html: subheading
                    }} />
                </FelaComponent>
            )}

            <LogoContainer layout={layout} contentAbove={heading || subheading}>
                {logos &&
                    logos.length &&
                    logos.map((logo, index) => {
                        if (!logo || !logo.logo) return null;

                        return (
                            <Visual
                                extend={logoStyle({
                                    collectionLength: logos.length,
                                    layout: layout
                                })}
                                load={preload}
                                image={logo.logo}
                                key={index}
                            />
                        );
                    })}
            </LogoContainer>
        </Container>
    );
}

const gardenSubheadingStyle = {
    width: "100%",
    marginTop: "15px"
};

const gardenHeadingStyle = () => ({
    width: "100%",
    marginBottom: 10,

    extend: [fluid("letter-spacing", { max: 2, min: 0.5 })]
});

const logoStyle = ({ collectionLength, layout }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 75,
    marginBottom: 20,
    ":last-of-type": {
        marginRight: 0
    },
    "& .vv-visual": {
        width: "100%",
        "& img": {
            maxHeight: 67
        }
    },
    "& .vv-image": {
        padding: 1
    },
    extend: [
        {
            condition: layout === "three_col",
            style: {
                minWidthTablet: {
                    grid: {
                        cols: 3,
                        margins: 75,
                        length: collectionLength,
                        bottomMargin: 30
                    }
                }
            }
        },
        {
            condition: layout === "four_col",
            style: {
                minWidthTablet: {
                    grid: {
                        cols: 4,
                        margins: 75,
                        length: collectionLength,
                        bottomMargin: 30
                    }
                }
            }
        },
        {
            condition: layout === "five_col",
            style: {
                minWidthTablet: {
                    grid: {
                        cols: 5,
                        margins: 75,
                        length: collectionLength,
                        bottomMargin: 30
                    }
                }
            }
        },
        {
            condition: layout === "six_col",
            style: {
                minWidthTablet: {
                    grid: {
                        cols: 6,
                        margins: 75,
                        length: collectionLength,
                        bottomMargin: 30
                    }
                }
            }
        }
    ],
    maxWidthTablet: {
        maxHeight: 40,
        justifyContent: "center",
        "& .vv-visual": {
            margin: "auto",
            maxWidth: 108,
            "& img": {
                maxHeight: 52
            }
        },
        grid: {
            cols: 2,
            margins: 40,
            bottomMargin: 30,
            length: collectionLength
        }
    }
});

const Container = createComponent(
    () => ({
        textAlign: "center",
        minHeight: 250,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        margin: "0 30px",
        borderRadius: "30px",
        "> *": {
            position: "relative",
            zIndex: 1
        },
        extend: [
            fluid("paddingV", gutters.vertical),
            fluid("paddingH", gutters.default)
        ]
    }),
    "div",
    ["id"]
);

const LogoContainer = createComponent(({ layout, contentAbove }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: 925,
    margin: "auto",
    extend: [
        {
            condition: layout === "three_col",
            style: {
                maxWidth: 600
            }
        },
        {
            condition: layout === "four_col",
            style: {
                maxWidth: 800
            }
        },
        {
            condition: contentAbove,
            style: {
                extend: [fluid("marginTop", { min: 30, max: 50 })]
            }
        }
    ],
    maxWidthTablet: {
        display: "flex",
        justifyContent: "center",
        maxWidth: 260,
        marginH: "auto"
    }
}));
